import { Content, type ContentType } from '@ilteducation/content-registry-types';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { useBooks } from '@ilteducation/books';
import { SearchContext, SearchQuery } from './types';
import { searchContent } from './api';
import { getQueryObject, getQueryString } from './query-formatters';
import { useClientAnalytics } from '@ilteducation/client-analytics';
import { v4 as uuidv4 } from 'uuid';

type SearchContextState<C extends Content> = {
  searchOperationId: string; // Used for analytics
  trackSearchQueries: boolean; // Whether this search operation is reported to analytics
  contentType: ContentType;
  searchQuery: SearchQuery;
  pageTotal: number;
  isLoading: boolean;
  contents: C[];
};

type State<C extends Content> = {
  [contextId: string]: SearchContextState<C> | undefined;
};

export const createSearchId = (contentType: ContentType, searchQuery: SearchQuery) =>
  `${contentType}:${getQueryString(searchQuery).slice(1)}`;

export const parseSearchId = (contextId: string): SearchContext => {
  const parts = contextId.split(/:(.*)/s);
  return { contentType: parts[0] as ContentType, searchQuery: getQueryObject(parts[1]) };
};
const useSearchContexts = <C extends Content>(contentType: ContentType) =>
  defineStore(`content-search-contexts-${contentType}`, () => {
    const clientAnalytics = useClientAnalytics();
    const { sendAnalyticsEvent } = clientAnalytics;

    const contextState = ref<State<C>>({});

    const context = computed(() => (contextId: string) => contextState.value[contextId]);
    const result = computed(() => (contextId: string) => contextState.value[contextId]?.contents ?? []);
    const isLoading = computed(() => (contextId: string) => contextState.value[contextId]?.isLoading ?? false);
    const hasMore = computed(() => (contextId: string) => {
      return contextState.value[contextId]?.searchQuery?.pagination?.cursor !== null;
    });
    const pageTotal = computed(() => (contextId: string) => contextState.value[contextId]?.pageTotal ?? 0);

    /**
     * @param trackSearchQueries - used to decide if we should report this search to analytics.
     * Set to true only when the user intentionally searches for content, like the search page, not for
     * collections, shelves, slug lookups and other places where we search on the user's behalf.
     */
    const search = (searchQuery: SearchQuery, limit: number, trackSearchQueries = false, source?: string) => {
      const contextId = createSearchId(contentType, searchQuery);

      if (isLoading.value(contextId)) {
        return;
      }
      const contextValue = context.value(contextId);
      if (
        contextValue !== undefined &&
        (contextValue.contents.length >= limit || contextValue.contents.length === contextValue.pageTotal)
      ) {
        return;
      }

      const query: SearchQuery = {
        ...searchQuery,
      };
      if (limit) {
        query.pagination = {
          ...searchQuery?.pagination,
          size: limit,
        };
      }

      contextState.value = {
        ...contextState.value,
        ...{
          [contextId]: {
            searchOperationId: uuidv4(),
            trackSearchQueries,
            contentType,
            searchQuery: query,
            pageTotal: 0,
            isLoading: true,
            contents: [],
          },
        },
      };

      searchContent<C>(contentType, query)
        .then(({ data, cursor, total }) => {
          const stateForThisContextId = contextState.value[contextId]!;

          contextState.value = {
            ...contextState.value,
            [contextId]: {
              ...stateForThisContextId,
              contentType,
              searchQuery: {
                ...stateForThisContextId.searchQuery,
                ...{ pagination: { cursor, size: limit } },
              },
              pageTotal: total,
              isLoading: false,
              contents: data,
            },
          };

          if (stateForThisContextId.trackSearchQueries) {
            sendAnalyticsEvent('SearchEventType', {
              searchOperationId: stateForThisContextId.searchOperationId,
              contentType: stateForThisContextId.contentType,
              originalSearchTerm: query.q,
              filters: query.filter,
              sortOptions: query.sort,
              searchResultsCount: total,
              source,
            });
          }

          const { addBooks } = useBooks();
          addBooks(data);
        })
        .catch(() => {
          contextState.value = {
            ...contextState.value,
            [contextId]: {
              ...contextState.value[contextId]!,
              isLoading: false,
            },
          };
        });
    };

    const next = (contextId: string) => {
      if (
        !contextState.value ||
        !contextState.value[contextId] ||
        isLoading.value(contextId) ||
        !hasMore.value(contextId)
      ) {
        return;
      }

      contextState.value = {
        ...contextState.value,
        [contextId]: {
          ...contextState.value[contextId]!,
          isLoading: true,
        },
      };

      searchContent<C>(contextState.value[contextId]!.contentType, contextState.value[contextId]!.searchQuery)
        .then(({ data, cursor }) => {
          contextState.value = {
            ...contextState.value,
            [contextId]: {
              ...contextState.value[contextId]!,
              searchQuery: {
                ...contextState.value[contextId]!.searchQuery,
                ...{ pagination: { cursor, size: contextState.value[contextId]!.searchQuery.pagination?.size || 100 } },
              },
              isLoading: false,
              contents: [...contextState.value[contextId]!.contents, ...data],
            },
          };
        })
        .catch(() => {
          contextState.value = {
            ...contextState.value,
            [contextId]: {
              ...contextState.value[contextId]!,
              isLoading: false,
            },
          };
        });
    };

    const clearSearch = () => {
      contextState.value = {};
    };

    /**
     * We send the position as a parameter, because sometimes the results are grouped or the display order is changed.
     *
     * @param contextId
     * @param clickedResultId - content id or a unique identifier for the clicked result. In case of videos, is composed of multiple video properties.
     * @param positionInSearchResults
     */
    const trackResultClick = (contextId: string, clickedResultId: string, positionInSearchResults: number) => {
      const stateForThisContextId = contextState.value[contextId];

      // Only report clicks on results from a search that was itself reported.
      if (!stateForThisContextId?.trackSearchQueries) {
        return;
      }

      sendAnalyticsEvent('SearchResultClickEventType', {
        searchOperationId: stateForThisContextId.searchOperationId,
        contentType: stateForThisContextId.contentType,
        searchResultId: clickedResultId,
        searchResultPosition: positionInSearchResults,
      });
    };

    return {
      search,
      next,
      clearSearch,
      context,
      result,
      isLoading,
      hasMore,
      total: pageTotal,
      trackResultClick,
    };
  })();

export default useSearchContexts;
